import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditNoteIcon from "@mui/icons-material/EditNote";

// Shown when an order already has at least one submitted check-in sheet: the user
// picks between starting another sheet for the samples still outstanding, or
// correcting one of the sheets already submitted.
//
// "Start a new check-in sheet" is only offered when samples are actually still
// outstanding -- once everything is checked in, correcting an existing sheet is
// the only sensible action (and used to be a dead end).
export default function CheckInSheetSelector({
  orderId,
  submittedSheets,
  totalSampleCount,
  remainingSampleCount,
  onStartNew,
  onEditSheet,
  onBack,
}) {
  const sheets = submittedSheets || [];
  const canStartNew = remainingSampleCount > 0;

  const formatDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed.toLocaleString();
  };

  const describeSamples = (sheet) => {
    if (!sheet.sampleCount) {
      // Sheets submitted before samples were linked to a receipt record have no
      // sample list to show -- say so rather than implying it covered nothing.
      return "Samples not recorded on this sheet";
    }

    const names = (sheet.samples || [])
      .map((s) => s.labId?.trim() || s.sampleIdentity?.trim() || `Sample ${s.sampleNumber}`)
      .filter(Boolean);

    const preview = names.slice(0, 4).join(", ");
    const more = names.length > 4 ? ` +${names.length - 4} more` : "";

    return `${sheet.sampleCount} sample${sheet.sampleCount === 1 ? "" : "s"}${
      preview ? ` — ${preview}${more}` : ""
    }`;
  };

  return (
    <Paper elevation={1} sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        rowGap={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="h6" fontWeight={600}>
          Previous check-in found
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={`${remainingSampleCount} of ${totalSampleCount} samples remaining`}
        />
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Order {orderId} already has {sheets.length} check-in sheet
        {sheets.length === 1 ? "" : "s"}. Start another sheet for the samples still
        outstanding, or update one of the sheets below.
      </Typography>

      <Button
        variant="contained"
        startIcon={<AddCircleOutlineIcon />}
        onClick={onStartNew}
        disabled={!canStartNew}
        sx={{ mb: 1 }}
      >
        Start a new check-in sheet
      </Button>
      {!canStartNew && (
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
          All {totalSampleCount} sample{totalSampleCount === 1 ? "" : "s"} on this order
          are already checked in, so there is nothing left to put on a new sheet.
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Update a submitted sheet
      </Typography>

      <List disablePadding>
        {sheets.map((sheet) => {
          const submittedOn = formatDate(sheet.dateReceied);

          return (
            <ListItem
              key={sheet.recordId}
              divider
              secondaryAction={
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditNoteIcon />}
                  onClick={() => onEditSheet(sheet)}
                >
                  Update
                </Button>
              }
            >
              <ListItemText
                primary={`Check-In Sheet #${sheet.sheetNumber}`}
                secondary={
                  <Box component="span" display="block">
                    <Box component="span" display="block">
                      {describeSamples(sheet)}
                    </Box>
                    <Box component="span" display="block">
                      {[submittedOn, sheet.userId?.trim() && `by ${sheet.userId.trim()}`]
                        .filter(Boolean)
                        .join(" · ")}
                    </Box>
                  </Box>
                }
              />
            </ListItem>
          );
        })}
      </List>

      {onBack && (
        <Box sx={{ mt: 2 }}>
          <Button onClick={onBack}>Back</Button>
        </Box>
      )}
    </Paper>
  );
}
